/**
 * The strip that sits between the filters and the table: what the result set
 * is, and the controls that shape how it's displayed rather than what it
 * contains. Filters change the data; these change the view.
 */
import type { Table as TanTable } from '@tanstack/react-table';
import {
  Check,
  Columns3,
  Download,
  Loader2,
  RefreshCw,
  Rows2,
  Rows3,
  Rows4,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Density } from './use-table-prefs';
import { cn } from '@/lib/utils';

export const toolbar_btn =
  'inline-flex h-8 items-center gap-1.5 rounded-control px-2.5 text-data font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40';

const DENSITIES: { value: Density; label: string; icon: typeof Rows2 }[] = [
  { value: 'compact', label: 'Compact', icon: Rows4 },
  { value: 'cozy', label: 'Cozy', icon: Rows3 },
  { value: 'comfortable', label: 'Comfortable', icon: Rows2 },
];

export function TableToolbar<T>({
  table,
  total,
  selected_count,
  density,
  on_density_change,
  on_reset_columns,
  is_fetching,
  on_refresh,
  on_export_page,
  on_export_all,
  is_exporting,
  extra,
}: {
  table: TanTable<T>;
  total: number;
  selected_count: number;
  density: Density;
  on_density_change: (d: Density) => void;
  on_reset_columns: () => void;
  is_fetching?: boolean;
  on_refresh?: () => void;
  on_export_page?: () => void;
  on_export_all?: () => void;
  is_exporting?: boolean;
  /** Page-specific buttons (e.g. "New banner"). */
  extra?: React.ReactNode;
}) {
  const hideable = table.getAllLeafColumns().filter((c) => c.getCanHide());
  const hidden_count = hideable.filter((c) => !c.getIsVisible()).length;
  const DensityIcon = DENSITIES.find((d) => d.value === density)?.icon ?? Rows3;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
      <div className="flex items-center gap-2 text-data text-muted-foreground">
        {/*
          Addressable on purpose: this number is what an operator cross-checks
          against a metric tile, and a mismatch between the two is exactly the
          kind of thing no screenshot would catch.
        */}
        <span className="tabular" data-testid="table-total">
          <span className="font-semibold text-foreground">{total.toLocaleString('en-IN')}</span>{' '}
          {total === 1 ? 'result' : 'results'}
        </span>
        {selected_count > 0 && (
          <span className="text-primary">· {selected_count} selected</span>
        )}
        {/* A refetch in the background must be visible but must not replace the
            table with a spinner — the old table is still correct until the new
            one lands. */}
        {is_fetching && (
          <span className="inline-flex items-center gap-1" role="status" aria-live="polite">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span className="sr-only">Refreshing results</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {extra}

        {(on_export_page || on_export_all) && (
          <DropdownMenu>
            <DropdownMenuTrigger className={toolbar_btn} disabled={is_exporting} data-testid="export-menu">
              {is_exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">Export</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export as CSV</DropdownMenuLabel>
              {on_export_page && (
                <DropdownMenuItem onSelect={on_export_page} data-testid="export-page">
                  <Download className="h-4 w-4" />
                  This page
                  <span className="ml-auto pl-3 text-micro text-muted-foreground tabular">
                    {table.getRowModel().rows.length}
                  </span>
                </DropdownMenuItem>
              )}
              {on_export_all && (
                <DropdownMenuItem onSelect={on_export_all} data-testid="export-all">
                  <Download className="h-4 w-4" />
                  All matching rows
                  <span className="ml-auto pl-3 text-micro text-muted-foreground tabular">
                    {total.toLocaleString('en-IN')}
                  </span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger className={toolbar_btn} data-testid="density-menu">
            <DensityIcon className="h-3.5 w-3.5" />
            <span className="sr-only">Row density</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Row height</DropdownMenuLabel>
            {DENSITIES.map((d) => (
              <DropdownMenuItem
                key={d.value}
                onSelect={() => on_density_change(d.value)}
                data-testid={`density-${d.value}`}
              >
                <d.icon className="h-4 w-4" />
                {d.label}
                {density === d.value && <Check className="ml-auto h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {hideable.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(toolbar_btn, hidden_count > 0 && 'text-primary')}
              data-testid="columns-menu"
            >
              <Columns3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                Columns{hidden_count > 0 ? ` (${hideable.length - hidden_count}/${hideable.length})` : ''}
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[13rem]">
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              {hideable.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={col.getIsVisible()}
                  // Radix closes on select by default; hiding four columns
                  // would mean reopening the menu four times.
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(v) => col.toggleVisibility(!!v)}
                  data-testid={`column-toggle-${col.id}`}
                >
                  {col.columnDef.meta?.label ?? col.id}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={on_reset_columns}>Reset to default</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {on_refresh && (
          <button
            type="button"
            className={toolbar_btn}
            onClick={on_refresh}
            disabled={is_fetching}
            data-testid="refresh"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', is_fetching && 'animate-spin')} />
            <span className="sr-only">Refresh</span>
          </button>
        )}
      </div>
    </div>
  );
}
